import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { useLanguage } from '@/contexts/LanguageContext';
import { MessageSquare, TrendingUp, Zap, Users, CheckCircle, ArrowRight, Bot, Target, Clock, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';

const AIChatbots = () => {
  const { t } = useLanguage();
  
  useSEO({
    title: "GPT-4 Powered Chatbots | 35% Conversion Increase",
    description: "LLM-powered chatbots delivering 35% conversion increases. Beyond basic bots - enterprise AI chatbot solutions. Free ROI calculator included.",
    keywords: "GPT-4 powered chatbots, LLM-powered customer service, AI chatbot ROI calculator, enterprise chatbots, chatbot marketing, conversational AI, automated customer support",
    ogTitle: "GPT-4 Powered Chatbots | 35% Higher Conversions",
    ogDescription: "Deploy intelligent chatbots that increase conversions by 35%. LLM-powered, enterprise-ready solutions with ROI calculator.",
    canonical: "https://quantumbox.ai/ai-chatbots"
  });

  const marketGrowth = [
    {
      metric: "23.3%",
      description: "CAGR 2025-2030",
      context: "Chatbot market growth"
    },
    {
      metric: "35%",
      description: "Conversion Increase",
      context: "GPT-4 powered bots"
    },
    {
      metric: "67%",
      description: "Cost Reduction",
      context: "vs Human support"
    }
  ];

  const evolutionComparison = [
    {
      category: "Basic Chatbots",
      capabilities: ["Keyword matching", "Pre-defined responses", "Limited understanding", "Frequent escalations"],
      limitations: ["90% escalation rate", "Poor user experience", "No learning capability"],
      color: "text-red-600"
    },
    {
      category: "LLM-Powered Chatbots",
      capabilities: ["Natural language understanding", "Contextual responses", "Continuous learning", "Complex problem solving"],
      limitations: ["35% higher conversions", "90% user satisfaction", "24/7 intelligent support"],
      color: "text-green-600"
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "35% Conversion Increase",
      description: "GPT-4 powered conversations that guide customers to purchase decisions",
      metric: "Proven ROI",
      case: "E-commerce client achieved 35% lift in 3 months"
    },
    {
      icon: Clock,
      title: "Instant Response Time",
      description: "No more waiting for human agents - intelligent responses in milliseconds",
      metric: "< 500ms",
      case: "99.9% uptime with sub-second responses"
    },
    {
      icon: Users,
      title: "Unlimited Conversations",
      description: "Handle thousands of simultaneous conversations without quality degradation",
      metric: "∞ Scale",
      case: "Black Friday: 10,000+ concurrent chats"
    },
    {
      icon: DollarSign,
      title: "67% Cost Reduction",
      description: "Dramatically reduce support costs while improving customer satisfaction",
      metric: "ROI 340%",
      case: "Enterprise client saved $2M annually"
    }
  ];

  const useCases = [
    {
      industry: "E-commerce",
      title: "Intelligent Sales Assistant",
      challenge: "Customers abandoning carts due to unanswered questions",
      solution: "GPT-4 chatbot provides instant product recommendations and support",
      results: {
        conversion: "+35%",
        cartAbandonment: "-45%",
        customerSat: "92%",
        responseTime: "< 2 seconds"
      },
      implementation: "2 weeks"
    },
    {
      industry: "SaaS",
      title: "Customer Success Bot",
      challenge: "Support team overwhelmed with repetitive onboarding questions",
      solution: "LLM-powered bot handles onboarding and technical support",
      results: {
        ticketReduction: "70%",
        onboardingTime: "-50%",
        userActivation: "+40%",
        supportCosts: "-$150K/year"
      },
      implementation: "3 weeks"
    },
    {
      industry: "Healthcare",
      title: "Patient Engagement Bot",
      challenge: "Patients unable to reach staff for appointment scheduling",
      solution: "HIPAA-compliant chatbot for appointments and basic inquiries",
      results: {
        appointmentBooking: "+60%",
        phoneCallVolume: "-40%",
        patientSatisfaction: "89%",
        staffEfficiency: "+25%"
      },
      implementation: "4 weeks"
    }
  ];

  const roiCalculator = {
    inputs: [
      { label: "Monthly Support Tickets", placeholder: "1000" },
      { label: "Average Resolution Time (minutes)", placeholder: "15" },
      { label: "Support Agent Hourly Rate ($)", placeholder: "25" },
      { label: "Current Conversion Rate (%)", placeholder: "3" }
    ],
    savings: [
      { label: "Monthly Support Cost Savings", value: "$18,750" },
      { label: "Additional Revenue from Conversions", value: "$52,500" },
      { label: "Total Monthly ROI", value: "$71,250" },
      { label: "Annual ROI", value: "$855,000" }
    ]
  };

  const implementation = [
    {
      step: 1,
      title: "Business Requirements Analysis",
      description: "Define chatbot objectives, use cases, and success metrics",
      timeline: "Week 1",
      deliverables: ["Requirements document", "Success metrics", "Integration plan"]
    },
    {
      step: 2,
      title: "LLM Configuration & Training",
      description: "Configure GPT-4 model with your business knowledge and tone",
      timeline: "Week 1-2",
      deliverables: ["Custom LLM model", "Knowledge base", "Response templates"]
    },
    {
      step: 3,
      title: "Integration & Testing",
      description: "Integrate with your systems and conduct comprehensive testing",
      timeline: "Week 2-3",
      deliverables: ["System integration", "Test results", "Performance metrics"]
    },
    {
      step: 4,
      title: "Launch & Optimization",
      description: "Deploy chatbot and continuously optimize based on performance data",
      timeline: "Week 3-4",
      deliverables: ["Live chatbot", "Analytics dashboard", "Optimization plan"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-5xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-secondary text-white border-0">
              Beyond Basic Bots • 23.3% Market CAGR
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              GPT-4 Powered Chatbots
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
              Deploy intelligent LLM-powered chatbots that increase conversions by 35%, 
              reduce support costs by 67%, and provide 24/7 customer engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button size="lg" asChild className="bg-gradient-to-r from-primary to-secondary hover:scale-105 transition-transform">
                <Link to="/assessment">Calculate Your ROI</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/contact">See Live Demo</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Market Growth Stats */}
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {marketGrowth.map((stat, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <div className="text-4xl font-bold text-primary mb-2">{stat.metric}</div>
                  <div className="text-lg font-medium mb-1">{stat.description}</div>
                  <div className="text-sm text-muted-foreground">{stat.context}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Evolution Comparison */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Beyond Basic Bots: The LLM Revolution</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Why businesses are abandoning basic chatbots for GPT-4 powered intelligent assistants
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {evolutionComparison.map((category, index) => (
              <Card key={index} className="relative overflow-hidden">
                <CardHeader>
                  <CardTitle className={`text-2xl ${index === 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {category.category}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Capabilities:</h4>
                      <ul className="space-y-1">
                        {category.capabilities.map((capability, capIndex) => (
                          <li key={capIndex} className="flex items-center gap-2 text-sm">
                            <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-red-500' : 'bg-green-500'}`} />
                            {capability}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">{index === 0 ? 'Limitations:' : 'Results:'}</h4>
                      <ul className="space-y-1">
                        {category.limitations.map((limitation, limIndex) => (
                          <li key={limIndex} className={`text-sm font-medium ${category.color}`}>
                            • {limitation}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Proven Business Impact</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Real results from enterprises using GPT-4 powered chatbots
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="relative overflow-hidden group hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20">
                      <benefit.icon className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{benefit.title}</CardTitle>
                      <Badge className="mt-1 bg-green-100 text-green-800">{benefit.metric}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-3">{benefit.description}</p>
                  <div className="text-sm text-blue-600 font-medium">
                    Case Study: {benefit.case}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Industry Success Stories</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              How leading companies are leveraging GPT-4 chatbots for competitive advantage
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <Card key={index} className="relative overflow-hidden">
                <CardHeader>
                  <Badge className="w-fit mb-2">{useCase.industry}</Badge>
                  <CardTitle className="text-xl">{useCase.title}</CardTitle>
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="font-medium text-red-600 mb-1">Challenge:</div>
                      <div className="text-muted-foreground">{useCase.challenge}</div>
                    </div>
                    <div>
                      <div className="font-medium text-blue-600 mb-1">Solution:</div>
                      <div className="text-muted-foreground">{useCase.solution}</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <div className="font-medium text-green-600 mb-2">Results:</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(useCase.results).map(([key, value], resultIndex) => (
                        <div key={resultIndex} className="flex justify-between">
                          <span className="text-muted-foreground">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span>
                          <span className="font-medium text-green-600">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Badge variant="secondary" className="w-fit">
                    Setup: {useCase.implementation}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator Preview */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Chatbot ROI Calculator</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Calculate your potential savings and revenue increase with GPT-4 powered chatbots
            </p>
          </div>
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Your Business Metrics</h3>
                  <div className="space-y-4">
                    {roiCalculator.inputs.map((input, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium mb-1">{input.label}</label>
                        <input 
                          type="text" 
                          placeholder={input.placeholder}
                          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4">Estimated ROI</h3>
                  <div className="space-y-4">
                    {roiCalculator.savings.map((saving, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <span className="text-sm font-medium">{saving.label}</span>
                        <span className="text-lg font-bold text-green-600">{saving.value}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="w-full mt-6" asChild>
                    <Link to="/assessment">Get Detailed ROI Analysis</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Deploy Intelligent Chatbots?</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Join thousands of businesses increasing conversions by 35% with GPT-4 powered chatbots. 
            Get your free ROI calculation and implementation roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-secondary hover:scale-105 transition-transform">
              <Link to="/assessment">
                Calculate Your ROI
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contact">Book Live Demo</Link>
            </Button>
          </div>
          <div className="mt-8 text-sm text-muted-foreground">
            ✓ Free ROI calculator ✓ Live chatbot demo ✓ 30-minute consultation
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIChatbots;